/**
 * Enrôlement TOTP OBLIGATOIRE des freelances (Lot A). Tant qu'il n'est pas
 * fait, le serveur ne répond qu'à l'enrôlement (bac à sable) — cet écran
 * remplace donc toute page protégée.
 *
 * Pas de QR code (aucune dépendance ajoutée) : la clé s'ajoute en « saisie
 * manuelle » dans l'application d'authentification, ou d'un tap via le lien
 * otpauth:// sur téléphone.
 */

import { useState } from "react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { KeyRound, LogOut } from "lucide-react";

/** Compte enrôlé mais session sans second facteur : reconnexion avec le code. */
export function MfaReauth() {
  const { logout } = useAuth();
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> Reconnexion requise</CardTitle>
          <CardDescription>
            Cette session a été ouverte sans votre code de double authentification. Reconnectez-vous
            avec votre mot de passe puis le code de votre application.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button className="w-full" onClick={() => void logout()}>
            <LogOut className="h-4 w-4 mr-2" /> Se reconnecter
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export function MfaEnrollment() {
  const { user, logout, refreshUser } = useAuth();
  const [secret, setSecret] = useState<{ secret: string; uri: string } | null>(null);
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  const setup = trpc.settings.setup2FA.useMutation({
    onSuccess: (r) => setSecret(r),
    onError: (e) => toast.error(e.message),
  });
  const verify = trpc.settings.verify2FA.useMutation({
    onSuccess: (r) => setBackupCodes(r.backupCodes),
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> Double authentification</CardTitle>
          <CardDescription>
            Obligatoire pour accéder à l'espace prospection ({user?.email}). Installez une application
            d'authentification (Google Authenticator, Microsoft Authenticator, 1Password…).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {backupCodes ? (
            <>
              <p className="text-sm">
                Double authentification activée. Conservez ces <strong>codes de secours</strong> en lieu sûr :
                chacun permet UNE connexion si vous perdez votre téléphone.
              </p>
              <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-muted rounded-md p-3">
                {backupCodes.map((c) => <span key={c}>{c}</span>)}
              </div>
              <Button className="w-full" onClick={() => void refreshUser()}>J'ai noté mes codes — continuer</Button>
            </>
          ) : !secret ? (
            <Button className="w-full" disabled={setup.isLoading} onClick={() => setup.mutate()}>
              {setup.isLoading ? "Génération…" : "Générer ma clé"}
            </Button>
          ) : (
            <>
              <ol className="text-sm space-y-2 list-decimal pl-5">
                <li>
                  Dans l'application, choisissez « Saisir une clé de configuration » et entrez :
                  <div className="font-mono text-base bg-muted rounded-md p-2 mt-1 break-all select-all">
                    {secret.secret.match(/.{1,4}/g)?.join(" ")}
                  </div>
                  <a href={secret.uri} className="text-primary underline text-xs">Sur téléphone : ouvrir directement l'application</a>
                </li>
                <li>Saisissez le code à 6 chiffres affiché :</li>
              </ol>
              <div className="space-y-1">
                <Label htmlFor="mfa-code">Code</Label>
                <Input id="mfa-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
              </div>
              <Button className="w-full" disabled={code.length !== 6 || verify.isLoading} onClick={() => verify.mutate({ token: code })}>
                Activer
              </Button>
            </>
          )}
          <Button variant="ghost" className="w-full" onClick={() => void logout()}>
            <LogOut className="h-4 w-4 mr-2" /> Se déconnecter
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
